import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { CompanyLocationsListPreviewOutDTO, FeaturesEnum, Pageable, UserCompanyOutDTO } from '@api/ringil-api.v2';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import COText from '@app/pages/COText/COText.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import useNavigation from '@hooks/useNavigation.tsx';
import AddEditLocationSidebar from './sidebars/AddEditLocationSidebar';
import { useCreateLocationACLSidebar } from './sidebars/LocationACLSidebar';
import SupersedeLocationDialog from './dialogs/SupersedeLocationDialog.tsx';
import VisibilityBadge from './component/VisibilityBadge';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';
import SvgPlus from '@icons/plus.svg?react';
import SvgArrowsRepeat from '@icons/arrows-repeat.svg?react';

/**
 * Tri-state the deleted dropdown stores into `showDeleted`. Resolved back to the boolean the API
 * expects (or omitted) at fetch time in normalizeFilter: DELETED → true, NOT_DELETED → false,
 * ALL → omit the field entirely so the backend returns both deleted and live locations.
 *
 * Local on purpose — the expedition list's ArchivedFilterStateEnum is the same shape but a different
 * screen's vocabulary.
 */
enum DeletedFilterStateEnum {
  ALL = 'all',
  DELETED = 'deleted',
  NOT_DELETED = 'notDeleted',
}

interface Filter {
  locationName?: string;
  showOnlyWithRamp?: boolean;
  showOnlyPartnerLocation?: boolean;
  /** Form field driving the deleted dropdown. Resolved to `showDeleted` (or omitted) in normalizeFilter. */
  deletedState?: DeletedFilterStateEnum;
  /** API output only — derived from `deletedState` at fetch time; not bound to any form control. */
  showDeleted?: boolean;
}

// RingilDataTable keys selection by `id`; the location row DTO exposes locationId
type LocationRow = CompanyLocationsListPreviewOutDTO & { id: string };

const LocalityList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getCompanyLocationsPaged, getCompanyForCurrentUser } = useContext(ApiContext);
  const navigation = useNavigation();

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: LocationRow[]; clear: () => void }>(null);
  const [addLocality, setAddLocality] = useState(false);
  const [companyData, setCompanyData] = useState<UserCompanyOutDTO>(null);
  const [supersedeLocation, setSupersedeLocation] = useState<LocationRow>(null);
  // Snapshot of the selection taken when the mass action fires — the table's selection lives inside
  // RingilDataTable and changing it does not re-render this page, so it cannot be read during render.
  const [aclLocationIds, setAclLocationIds] = useState<string[]>([]);

  // Feature-gated only, like the neighbouring Delete action which carries no permission gate either.
  const supersedeFlag = useSecuredFlag('location-list-supersede', {
    kind: 'action',
    description: 'Replace a location by another one',
    features: [FeaturesEnum.SUPERSEDE_LOCATION],
  });

  const { setVisible: setVisibleACLSidebar, LocationACLSidebar } = useCreateLocationACLSidebar();

  // Loaded only when the add-locality sidebar opens, and only to prefill its country/timezone.
  // On page load this fired for everyone, and a user who may see locations but does not hold the
  // company-profile permission got a 403 — i.e. an error toast on a page that works fine without
  // the data. A failure here simply means no prefill, so it must stay silent. (Sentry RINGIL-APP-SX.)
  useEffect(() => {
    if (!addLocality || companyData != null) return;
    getCompanyForCurrentUser(
      data => {
        setCompanyData(data);
      },
      null,
      { onError: () => {} },
    );
  }, [addLocality]);

  /**
   * `deletedState` is UI-only; never send it. Stored filters predate it and arrive as `undefined`,
   * which the fallback below resolves to the default — there is no legacy boolean to migrate from.
   */
  const normalizeFilter = (value?: Filter): Filter => {
    const base = { ...(value ?? {}) };
    const deletedState = base.deletedState ?? DeletedFilterStateEnum.NOT_DELETED;

    const result: Filter = { ...base };
    delete result.deletedState;
    if (deletedState === DeletedFilterStateEnum.ALL) {
      delete result.showDeleted;
    } else {
      result.showDeleted = deletedState === DeletedFilterStateEnum.DELETED;
    }
    return result;
  };

  const loadData = (filter: Filter, pageable: Pageable, onSuccess: (pagedData: PagedData<LocationRow>) => void) => {
    getCompanyLocationsPaged(filter?.locationName, filter?.showOnlyWithRamp, filter?.showOnlyPartnerLocation, filter?.showDeleted, pageable, data =>
      onSuccess({ ...data, data: data.data.map(t => ({ ...t, id: t.locationId })) }),
    );
  };

  const toDetail = (id: string) => {
    navigation.navigate(navigation.urlFunctions.createLocalityDetail(id));
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('LocalityList.name', 'Name'),
      value: ['locationName'],
      dataTestId: 'location-list-filter-name',
    },
    {
      filterType: FilterType.Checkbox,
      placeholder: tr('LocalityList.jenSRampou', 'Jen s rampou'),
      value: ['showOnlyWithRamp'],
      dataTestId: 'location-list-filter-withRamp',
    },
    {
      filterType: FilterType.Checkbox,
      placeholder: tr('LocalityList.showOnlyPartnerLocation', 'Only partner locations'),
      value: ['showOnlyPartnerLocation'],
      dataTestId: 'location-list-filter-partner',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('LocalityList.deletedState', 'Deleted'),
      value: ['deletedState'],
      options: [
        { label: tr('LocalityList.deletedStateNotDeleted', 'Not deleted'), value: DeletedFilterStateEnum.NOT_DELETED, active: true },
        { label: tr('LocalityList.deletedStateDeleted', 'Only deleted'), value: DeletedFilterStateEnum.DELETED, active: true },
        { label: tr('LocalityList.deletedStateAll', 'All'), value: DeletedFilterStateEnum.ALL, active: true },
      ],
      dataTestId: 'location-list-filter-deleted',
    },
  ];

  const columnConfig: ColumnConfig<LocationRow>[] = [
    {
      id: 'name',
      field: 'name',
      header: 'LocalityList.name',
      headerDefault: 'Name',
      width: '30%',
      sortable: true,
      isDefaultSort: true,
      defaultSortDirection: 'ASC',
      order: 1,
      render: row => (
        <div className={'flex flex-col items-start gap-2'}>
          <Link
            to={navigation.createNavigationLink(navigation.urlFunctions.createLocalityDetail(row.locationId))}
            className={clickableTextClass}
            data-testid={`location-list-name-${row.locationId}`}>
            {row.name}
          </Link>
          <div className={'flex flex-row flex-wrap items-center gap-2'}>
            <VisibilityBadge acl={row.acl} dataTestId={`location-badge-visibility-${row.locationId}`} />
            {/* A superseded location is a deleted one; the forwarding badge replaces the plain "Deleted" one. */}
            {row.supersededBy ? (
              <COBadge
                variant={'filled'}
                type={'custom'}
                className={'bg-complementary-warningMedium text-content-primary cursor-pointer'}
                onClick={() => toDetail(row.supersededBy.id)}
                dataTestId={`location-badge-supersededBy-${row.locationId}`}>
                <COText label={tr('LocalityList.supersededBy', 'Replaced by {{name}}', { name: row.supersededBy.name })} />
              </COBadge>
            ) : (
              row.isDeleted && (
                <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-error text-white'} dataTestId={`location-badge-deleted-${row.locationId}`}>
                  <COText label={tr('LocalityList.deleted', 'Deleted')} />
                </COBadge>
              )
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'contactName',
      field: 'contactName',
      header: 'LocalityList.contactName',
      headerDefault: 'Contact name',
      width: '20%',
      order: 2,
      render: row => <span>{row.contactName}</span>,
    },
    {
      id: 'address',
      field: 'address',
      header: 'LocalityList.address',
      headerDefault: 'Address',
      width: '42%',
      order: 3,
      render: row => (
        <span>
          {row.streetNr}, {row.postalCode} {row.city}, {row.country}, {row.timezoneId}
        </span>
      ),
    },
    {
      id: 'action',
      field: 'action',
      width: '8%',
      order: 4,
      verticalAlign: 'middle',
      render: row => (
        <div className={'flex flex-row flex-wrap justify-end gap-2'}>
          <SecuredArea
            id="location-list-supersede" instance={row.locationId}
            label={'Replace location'} description="Replace the location with a new one"
            visible={[supersedeFlag, { type: 'data', label: 'Location is not deleted', satisfied: !row.isDeleted }]}>
            <RingilButton
              mode={'table'}
              icon={<SvgArrowsRepeat />}
              tooltip={tr('LocalityList.supersede', 'Replace…')}
              onClick={() => setSupersedeLocation(row)}
              dataTestId={`location-list-supersede-${row.locationId}`}
            />
          </SecuredArea>
        </div>
      ),
    },
  ];

  const massActionConfig: MassActionConfig<LocationRow>[] = [
    {
      label: tr('LocalityList.setVisibility', 'Set visibility'),
      onClick: () => {
        const ids = (tableRef.current?.selectedObjects ?? []).map(t => t.locationId);
        if (ids.length === 0) return;
        setAclLocationIds(ids);
        setVisibleACLSidebar(true);
      },
    },
  ];

  return (
    <>
      <BreadCrumbComponent items={[{ label: tr('index.localityList', 'Locality list') }]} />
      <div className={'flex items-center'}>
        <Heading title={tr('LocalityList.localities', 'Localities')} headingVariant={'h1'} />
        <RingilButton
          mode={'link'}
          icon={<SvgPlus />}
          label={tr('LocalityList.addLocation', 'Add location')}
          onClick={() => setAddLocality(true)}
          dataTestId={'location-list-add'}
        />
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadData}
        filterName={'locality_list'}
        normalizeFilter={normalizeFilter}
        massActionConfig={massActionConfig as MassActionConfig<unknown>[]}
        // A deleted location is read-only, so it cannot take part in the visibility mass action.
        isRowSelectable={(row: unknown) => !(row as LocationRow).isDeleted}
      />

      <AddEditLocationSidebar
        visible={addLocality}
        onHide={() => setAddLocality(false)}
        onCompleted={() => {
          setAddLocality(false);
          tableRef.current?.reloadData();
        }}
        country={companyData?.country}
        timezoneId={companyData?.defaultTimezoneId}
      />

      <LocationACLSidebar
        onCompleted={() => {
          tableRef.current?.clear();
          tableRef.current?.reloadData();
        }}
        locationIds={aclLocationIds}
      />

      {supersedeLocation && (
        <SupersedeLocationDialog
          visible={true}
          locationId={supersedeLocation.locationId}
          locationName={supersedeLocation.name}
          onHide={() => setSupersedeLocation(null)}
          onCompleted={() => {
            setSupersedeLocation(null);
            tableRef.current?.clear();
            tableRef.current?.reloadData();
          }}
        />
      )}
    </>
  );
};

export default LocalityList;
